import { GenderType, MSSMemberStatus, RoleType } from '@ace-clublabs/common-membership-api-types';
import {
  ManageMembershipItem,
  ManageMembershipItemButton,
  ManageMembershipItemInfo,
} from '../ManageMembershipItem/ManageMembershipItem';
import { setGlobalState, useGlobalState } from '../../../state/state';
import { ROLE_TYPE_MAP } from '../../../utils/stringHelper';
import { useHistory } from 'react-router-dom';
import { MSSMembers } from '@lib/types';
import { getMaximumAssociatesAllowed } from '../../../state/helpers';
import { Pages } from '../../../utils/constants';

export const FamilyMembers = () => {
  const history = useHistory();
  const [membershipDetails] = useGlobalState('memberDetails');

  const { loggedInCustomerId, prospectiveMemberRules } = membershipDetails;

  const { downgradesAllowed } = prospectiveMemberRules;

  const [formData] = useGlobalState('formData');
  const { members } = formData;

  const deleteMember = (member: MSSMembers) => {
    let filteredMembers = members?.filter((mem) => mem.virtualId !== member.virtualId);

    if (member.customerId) {
      filteredMembers = members?.map((mem) =>
        mem.customerId === member.customerId ? { ...mem, status: MSSMemberStatus.REMOVED } : mem
      );
    }

    setGlobalState('formData', { ...formData, members: filteredMembers });
  };

  const associates =
    formData?.members?.filter((mem) => mem.roleType !== RoleType.PRIMARY && mem.status !== MSSMemberStatus.REMOVED) ||
    [];

  const memberItems = associates?.map((member) => {
    const { firstName, middleName, lastName, nameSuffix, roleType, status } = member;
    const formattedMiddleInitial = middleName ? `${middleName.charAt(0).toUpperCase()} ` : '';
    const suffix = nameSuffix ? `, ${nameSuffix.toUpperCase()}` : '';
    const memberFullDisplayName = `${firstName} ${formattedMiddleInitial}${lastName}${suffix}`;
    return {
      memberName: memberFullDisplayName,
      firstName: member.firstName,
      lastName: member.lastName,
      memberRole: roleType,
      customerId: member.customerId ?? '',
      virtualId: member.virtualId ?? '',
      dateOfBirth: member.dateOfBirth,
      gender: member.gender,
      email: member.emailAddress,
      phoneNumbers: member.phoneNumbers,
      status,
    };
  });

  const MEMBER_HIERARCHY = [RoleType.ADULT_ASSOCIATE, RoleType.DEPENDENT, RoleType.ASSOCIATE];
  const orderedMemberItems = memberItems.sort((a, b) => {
    if (a.status === MSSMemberStatus.ADDED || b.status === MSSMemberStatus.ADDED) {
      return 0;
    }
    return MEMBER_HIERARCHY.indexOf(a.memberRole) - MEMBER_HIERARCHY.indexOf(b.memberRole);
  });

  const membersProps = orderedMemberItems?.map((member, i) => ({
    type: 'info',
    title: member.memberName,
    subtitle: ROLE_TYPE_MAP[member.memberRole],
    className: `family-member-${i}`,
    key: `family-member-${i}`,
    leftIconName: member.status === MSSMemberStatus.ADDED ? 'pen-regular' : undefined,
    leftIconOnClick: () => {
      history.push({
        pathname: Pages.AssociateForm,
        state: { associate: member, isEditing: true },
      });
    },
    rightIconName:
      (downgradesAllowed && member.customerId !== loggedInCustomerId) || member.status === MSSMemberStatus.ADDED
        ? 'xmark-regular'
        : undefined,
    rightIconOnClick: () => {
      deleteMember(member as unknown as MSSMembers);
    },
    mask: true,
  })) as (ManageMembershipItemInfo | ManageMembershipItemButton)[];

  const canAddMoreAssociates = associates.length < getMaximumAssociatesAllowed(membershipDetails);
  if (canAddMoreAssociates) {
    const addNewMemberButton = {
      type: 'button',
      text: 'Add',
      iconName: 'plus-regular',
      className: `add-family-member`,
      onClick: () => {
        history.push(Pages.RoleTypeSelection);
      },
    } as ManageMembershipItemButton;

    membersProps.push(addNewMemberButton);
  }

  return (
    <ManageMembershipItem
      sectionTitle="Family members"
      iconName="user-group-duotone"
      titleClassName="family-members"
      items={membersProps}
    />
  );
};
